import { useEffect, useState } from "react";
import MultiRangeSlider from "./MultiRangeSlider"; // adjust the path accordingly
import { getCatSubCat } from "../../../api";
import { loginLogout, useProjectByStorage } from "../../../projectData";


export const RedemFilterButton = ({ setRedemFilters, redemFilters, cid }) => {
    const project = useProjectByStorage();

    const [minValue] = useState(10);
    const [maxValue] = useState(3000000);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const defaultFilters = {
        minPoints: 1,
        maxPoints: 3000000,
        category: "",
        /**
         * RedemFilterButton component
         * Provides a filter drawer for redemption items, allowing users to filter by points range, category, sub-category, and sort options.
         * Handles fetching categories, managing filter state, and submitting filter criteria.
         */
        cid: cid || "",
        sortOrder: "",
        sortByOffer: 'on_offer'
    };

    const [filterValue, setFilterValue] = useState(defaultFilters);


    // Fetch categories on mount or when project changes
    useEffect(() => {
        if (!project?.pkey) return;

        getCatSubCat({ project_key: project.pkey }).then((response) => {
            setCategories(response?.data || []);
        })
            .catch((error) => {
                loginLogout(error.response)
            })
    }, [project]);

    const handleCategoryChange = (e) => {
        const selectedCategory = e.target.value;

        setFilterValue((prev) => ({
            ...prev,
            category: selectedCategory,
            cid: "" // Reset sub-category when category changes
        }));

        getCatSubCat({ categoryID: selectedCategory, project_key: project?.pkey }).then((d) => {
            setSubCategories(d?.data || [])
        }).catch((error) => {
            loginLogout(error.response)
        })
    };

    const handleInputChange = ({ target: { name, value } }) => {
        setFilterValue((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSortChange = (sortOrder) => {
        setFilterValue((prev) => ({
            ...prev,
            sortOrder
        }));
    };

    const handleOfferChange = (sortByOffer) => {
        setFilterValue((prev) => ({
            ...prev,
            sortByOffer
        }));
    };

    const handleRangeChange = ({ min, max }) => {
        setFilterValue((prev) => ({
            ...prev,
            minPoints: min,
            maxPoints: max
        }));
    };

    const handleReset = () => {
        setFilterValue(defaultFilters);
        setRedemFilters(defaultFilters);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setRedemFilters((prev) => ({
            ...prev,
            ...filterValue
        }));
        setIsDrawerOpen(false);
    };

    const drawerStyles = {
        position: "fixed",
        top: 0,
        right: isDrawerOpen ? 0 : "-102%",
        width: "100%",
        height: "100%",
        backgroundColor: "#fff",
        transition: "right 0.3s ease",
        zIndex: 1000,
        boxShadow: "-2px 0 5px rgba(0,0,0,0.5)"
    };

    const overlayStyles = {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 999
    };

    return (
        <>
            <button onClick={() => setIsDrawerOpen(true)} className="btn btn-dark btn-flex h-50px px-4 mt-4">
                <i className="bi bi-funnel-fill fs-2x" />
            </button>

            {/* Drawer */}
            <div className="card rounded-0" style={drawerStyles}>
                <div className="d-flex justify-content-between mt-8 px-6 pb-4 border-bottom">
                    <h3>Filter Options</h3>
                    <button onClick={() => setIsDrawerOpen(false)} className="btn-close" aria-label="Close" />
                </div>

                <form onSubmit={handleSubmit} className="card-body pt-2">
                    {/* Sort Order */}
                    <FilterSection title="Sort By Points">
                        <SortButtons current={filterValue.sortOrder} options={[
                            { label: "Low to High", value: "low_to_high" },
                            { label: "High to Low", value: "high_to_low" }
                        ]} onSelect={handleSortChange} />
                    </FilterSection>

                    {/* Featured / Offer */}
                    <FilterSection title="Filter by">
                        <SortButtons current={filterValue.sortByOffer} options={[
                            { label: "FEATURED", value: "featured" },
                            { label: "ON OFFER", value: "on_offer" }
                        ]} onSelect={handleOfferChange} />
                    </FilterSection>

                    {/* Points Range */}
                    <FilterSection title="Points Range">
                        <div className="d-flex gap-4 mb-2">
                            <input type="number" className="form-control" name="minPoints" value={filterValue.minPoints} onChange={handleInputChange} placeholder="Min Points" />
                            <input type="number" className="form-control" name="maxPoints" value={filterValue.maxPoints} onChange={handleInputChange} placeholder="Max Points" />
                        </div>
                        <MultiRangeSlider min={minValue} max={maxValue} onChange={handleRangeChange} />
                    </FilterSection>

                    {/* Categories */}
                    <FilterSection title="Sort by Category">
                        <div className="d-flex flex-column flex-md-row gap-2">
                            <select className="form-select" name="category" value={filterValue.category} onChange={handleCategoryChange}>
                                <option value="">Select Category</option>
                                {categories.map((cat) => (
                                    <option key={cat.ID} value={cat.ID}>{cat.CatName}</option>
                                ))}
                            </select>

                            <select className="form-select" name="cid" value={filterValue.cid} onChange={handleInputChange} disabled={!filterValue.category}>
                                <option value="">Select Sub-category</option>
                                {subCategories.map((sub) => (
                                    <option key={sub.ID} value={sub.ID}>{sub.subCatName}</option>
                                ))}
                            </select>
                        </div>
                    </FilterSection>

                    {/* Buttons */}
                    <div className="d-flex gap-3 mt-4">
                        <button type="button" className="btn btn-secondary w-100" onClick={handleReset}>Reset</button>
                        <button type="submit" className="btn btn-primary w-100">Submit</button>
                    </div>
                </form>
            </div>

            {/* Overlay */}
            {isDrawerOpen && <div style={overlayStyles} onClick={() => setIsDrawerOpen(false)} />}
        </>
    );
};

// Reusable FilterSection component
const FilterSection = ({ title, children }) => (
    <div className="row p-4 border rounded mb-4" style={{ border: "1px solid #585858" }}>
        <div className="col-lg-12">
            <h5 className="fw-semibold mb-3">{title}</h5>
            {children}
        </div>
    </div>
);

// Reusable SortButtons component
const SortButtons = ({ options, current, onSelect }) => (
    /**
     * FilterSection component
     * Reusable section wrapper for filter UI blocks with a title.
     */
    <div className="d-flex gap-3 flex-wrap">
        {options.map(({ label, value }) => (
            <button
                key={value}
                type="button"
                className={`btn btn-sm flex-grow-1 ${current === value ? "btn-primary" : "btn-secondary"}`}
                onClick={() => onSelect(value)}
            >
                {label}
            </button>
        ))}
    </div>
);
